import {useCallback, useEffect, useState} from 'react';

import styled, {useTheme, DefaultTheme} from 'styled-components';
import {useDispatch} from 'react-redux';

import {addBillingPermissionUser, getBillingPermissionsList, removeBillingPermissionUser} from '~/shared/services/bffService/api';
import {attachModal} from '~/shared/components/ReduxModal';
import {getLocalizationService} from '~/shared/services/localisationService';
import actions from '~/shared/store/actions';
import {media} from '~/shared/theme/media';
import ClickOutsideDiv from '~/shared/components/ClickOutsideDiv';
import {body14Normal, body16Bold, body18Bold, body20Bold} from '~/shared/theme/typography';
import PieCloseModalButton from '~/shared/components/Modals/styled/PieCloseModalButton';
import {BillingPermissionsListResponse, MoneyCard} from '~/shared/services/bffService/api/interfaces/responses';
import {flexColumn} from '~/shared/theme/FlexLayout';
import FormPieInput from '~/shared/components/FormField/FormInput/FormPieInput';
import {createLogger} from '~/shared/logging/logging';
import PieButton from '~/shared/theme/pie/PieButton/PieButton';
import ImageWithAlt from '~/shared/components/ImageWithAlt';
import User from '~/shared/store/models/User/User';
import {handleRefreshToken} from '~/shared/services/auth/auth';
import {is401Error} from '~/shared/services/apiErrorService';

import checkCircleFilledIcon from './assets/checkCircleFilled.svg';
import alertCircleFilledIcon from './assets/alertCircleFilled.svg';
import minusCircleFilledIcon from './assets/minusCircleFilled.svg';

const logger = createLogger('BillingPermissionsModal');

const Root = styled(ClickOutsideDiv)`
  display: flex;
  flex-direction: column;

  &&& {
    width: 100vw;

    /* Use dynamic viewport height so mobile browser chrome (e.g. Chrome's address bar)
       shrinks the modal to the visible area instead of clipping the centered header off-screen. */
    height: 100dvh;
    border-radius: 0;
    padding-top: 0;
    padding-bottom: 8px;
  }

  &:first-child {
    background-color: ${({theme}) => theme.colors.surface};
  }

  ${media.minLargeTablet`
    &&& {
      width: inherit;
      height: inherit;
    }
    max-width: 483px;
    justify-content: space-between;
    border-radius: 2px;
    box-shadow: ${({theme}) => theme.shadows.shadow1};
  `}
`;

const Header = styled.div`
  height: 64px;
  width: 100%;
  padding: 16px 0;
  background-color: ${({theme}) => theme.colors.surface};
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  line-height: 16px;

  svg {
    ${({theme}) => `
      fill: ${theme.colors.text2};
    `}
  }
`;

const Title = styled.h3`
  color: ${({theme}) => theme.colors.text2};
  ${body18Bold}
  text-align: center;
  width: 100%;
  ${media.minLargeTablet`
    ${body20Bold}  
  `}
`;

const ListWrapper = styled.div`
  flex: 1;
  padding: 32px 16px 0 16px;
  overflow-x: hidden;
  ${media.minLargeTablet`
    padding: 32px;
    overflow-y: auto;
    max-height: 60vh;
  `}
`;

const List = styled.ul`
  padding: 0;
  margin: 0;
  list-style: none;
  flex-direction: column;
  display: flex;
  gap: 24px;
  margin-top: 32px;
`;

const AddressItem = styled.li`
  width: 100%;
  margin: 0;
  padding: 0;
  display: flex;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  padding-bottom: 20px;
  flex: 1;
  justify-content: space-between;

  &:last-child {
    border-bottom: 0;
    padding: 0 0 1px 0;
  }
`;

const SubTitle = styled.h3`
  ${body16Bold}
`;

const CloseButton = styled(PieCloseModalButton)`
  top: 16px;
`;

const Content = styled.div`
  ${flexColumn}
`;

const SubTitle2 = styled(SubTitle).attrs({as: 'h4'})`
  ${body14Normal}
  color: rgba(0, 0, 0, 0.64);
`;

const Form = styled.div`
  margin-top: 24px;
`;

const StatusRoot = styled.div<{bgColor: string}>`
  width: 100%;
  height: 44px;
  padding: 12px;
  background-color: ${({bgColor}) => bgColor};
  gap: 8px;
  display: flex;
  border-radius: 12px;
  ${body14Normal}
  line-height: 21px;
  margin-top: 24px;
`;

const StyledRemoveButton = styled(PieButton).attrs({
  variant: 'outlineSmall',
})`
  height: 32px;
`;

const BillingPermissionStatus = ({text, bgColor, iconUrl}: {text: string; bgColor: string; iconUrl: string}) => {
  // isAddedUserAction = false -> removeUser action.
  return (
    <StatusRoot bgColor={bgColor}>
      <ImageWithAlt src={iconUrl} noAlt />
      {text}
    </StatusRoot>
  );
};

enum statues {
  USER_ADD_SUCCESSFULLY = 'user_add_successfully',
  USER_ADD_FAILED = 'user_add_failed',
  USER_REMOVE_SUCCESSFULLY = 'user_remove_successfully',
  USER_REMOVE_FAILED = 'user_remove_failed',
}

type Colors = DefaultTheme['colors'];
type ColorKey = {[K in keyof Colors]: Colors[K] extends string ? K : never}[keyof Colors];

type StatusConfig = {
  translationKey: string;
  icon: string;
  bgColorKey: ColorKey;
};

const statusMap: Partial<Record<statues, StatusConfig>> = {
  [statues.USER_ADD_SUCCESSFULLY]: {
    translationKey: 'billing_permission_added_successfully',
    icon: checkCircleFilledIcon,
    bgColorKey: 'feedbackSupportPositiveTonal',
  },
  [statues.USER_ADD_FAILED]: {
    translationKey: 'billing_permission_added_failed',
    icon: alertCircleFilledIcon,
    bgColorKey: 'feedbackSupportErrorTonal',
  },
  [statues.USER_REMOVE_SUCCESSFULLY]: {
    translationKey: 'billing_permission_removed_successfully',
    icon: minusCircleFilledIcon,
    bgColorKey: 'supportInfoTonal',
  },
  [statues.USER_REMOVE_FAILED]: {
    translationKey: 'billing_permission_removed_failed',
    icon: alertCircleFilledIcon,
    bgColorKey: 'feedbackSupportErrorTonal',
  },
};

const BillingPermissionsModal = ({args: {moneycardId}}: {args: {moneycardId: MoneyCard['id']}}) => {
  const [list, setList] = useState<BillingPermissionsListResponse['linkedUsers']>([]);
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<statues | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  const theme = useTheme();

  const {t} = getLocalizationService();
  const dispatch = useDispatch();

  const handleCloseModal = useCallback(() => {
    dispatch(actions.setCurrentModal(null));
  }, [dispatch]);

  const fetchBillingPermissionsList = useCallback(async () => {
    try {
      const responseList = await getBillingPermissionsList(moneycardId);
      setList(responseList.linkedUsers);
    } catch (error) {
      if (is401Error(error)) {
        await handleRefreshToken(error, () => fetchBillingPermissionsList());
      }
    }
  }, [moneycardId]);

  useEffect(() => {
    fetchBillingPermissionsList();
  }, [fetchBillingPermissionsList]);

  const onAddAuthorizedUser = async () => {
    try {
      setServerError(null);
      setStatus(null);

      await addBillingPermissionUser(moneycardId, email);
      await fetchBillingPermissionsList();
      setStatus(statues.USER_ADD_SUCCESSFULLY);
    } catch (error: any) {
      if (is401Error(error)) {
        await handleRefreshToken(error, onAddAuthorizedUser);
        return;
      }

      if (error.message) {
        setStatus(statues.USER_ADD_FAILED);
        setServerError(error.message);
      }

      logger.warn('Failed to add billing permission user or fetching the list:', error);
    }
  };

  const onRemoveAuthorizedUser = async (userId: User['userId']) => {
    try {
      setServerError(null);
      setStatus(null);

      await removeBillingPermissionUser(moneycardId, userId);
      await fetchBillingPermissionsList();

      setStatus(statues.USER_REMOVE_SUCCESSFULLY);
    } catch (error) {
      if (is401Error(error)) {
        await handleRefreshToken(error, () => onRemoveAuthorizedUser(userId));
        return;
      }

      setStatus(statues.USER_REMOVE_FAILED);
      logger.warn('Failed to remove billing permission user or fetching the list:', error);
    }
  };

  const statusConfig = status ? statusMap[status] : null;

  return (
    <Root onClickOutside={handleCloseModal}>
      <Header>
        <Title id="modal-title">
          {t('manage_billing_permissions')}
        </Title>
        <CloseButton onClick={handleCloseModal} />
      </Header>

      <ListWrapper>
        <Content>
          <SubTitle>{t('add_billing_authorized_user')}</SubTitle>
          <SubTitle2>{t('insert_email_address_to_add_billing_authorized_user')}</SubTitle2>
        </Content>
        <Form>
          <FormPieInput
            id="email-field"
            placeholder={t('email')}
            value={email || ''}
            onChange={input => setEmail(input.target.value)}
            aria-autocomplete="none"
            aria-label="Insert email"
            data-test-id="email-field"
          />
          <input type="submit" value={t('add')} onClick={onAddAuthorizedUser} disabled={!email} />
        </Form>
        {statusConfig && (
          <BillingPermissionStatus
            bgColor={theme.colors[statusConfig.bgColorKey]}
            text={serverError || t(statusConfig.translationKey)}
            iconUrl={statusConfig.icon}
          />
        )}
        {Boolean(list.length) && (
          <List>
            {list.map(user => (
              <AddressItem key={user.userId}>
                {user.fullName}
                <StyledRemoveButton onClick={() => onRemoveAuthorizedUser(user.userId)}>{t('remove')}</StyledRemoveButton>
              </AddressItem>
            ))}
          </List>
        )}
      </ListWrapper>
      
    </Root>
  );
};

BillingPermissionsModal.hideCloseButton = true;

attachModal('billingPermissions', BillingPermissionsModal);

export default BillingPermissionsModal;
